import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';
import {
  AddIconComponent,
  CloseSquareIconComponent,
  DeleteIconComponent,
  TickSquareIconComponent,
} from '@kuiper/icons';
import { PositionListItem } from '../position.model';
import { PositionService } from '../position.service';
import {
  ACTIONS_INDEXES,
  ACTIONS_MENU_ITEMS,
  POSITION_LIST_COLUMN_IDENTIFIERS,
  POSITION_LIST_COLUMNS,
  POSITION_LIST_FILTER_TABS,
} from './position-list.constants';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { FilteredTableComponent } from '../../../../../common/components/filtered-table/filtered-table.component';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { FilterTableColByFeatFlagPipe } from '../../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { DEFAULT_PAGE_SIZE } from '../../../../../common/constants/pagination.constants';
import {
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ENDPOINTS } from '../../../../../common/constants/endpoints.constants';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import {
  ApiResponse,
  Meta,
} from '../../../../../common/models/api-response.model';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../../ui-components/lib/table/table.model';
import { FilteredTablePayload } from '../../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-position-list',
  imports: [
    TranslocoPipe,
    FilteredTableComponent,
    BadgeComponent,
    ActionListComponent,
    RouterModule,
    ReactiveFormsModule,
    PaginatedSelectComponent,
    ValidationMessageComponent,
    AvatarComponent,
    AddIconComponent,
    CloseSquareIconComponent,
    DeleteIconComponent,
    TickSquareIconComponent,
    TenantPathPipe,
    UserStatusPipe,
    FilterTableColByFeatFlagPipe,
  ],
  templateUrl: './position-list.component.html',
  styleUrl: './position-list.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PositionListComponent {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  readonly POSITION_LIST_COLUMN_IDENTIFIERS = POSITION_LIST_COLUMN_IDENTIFIERS;

  private readonly transloco = inject(TranslocoService);
  private readonly positionService = inject(PositionService);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  deactivateModal = viewChild<TemplateRef<unknown>>('deactivate');
  activateModal = viewChild<TemplateRef<unknown>>('activate');
  deleteModal = viewChild<TemplateRef<unknown>>('deleteRecord');

  readonly isDeleteInactiveEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .delete_inactive_groups_departments_job_titles_and_positions
    )
  ).asReadonly();

  readonly isPositionStatuesEnhancementEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.position_statues_enhancement
    )
  ).asReadonly();

  isLoading = signal<boolean>(false);
  positionList = signal<PositionListItem[]>([]);
  meta = signal<Meta | undefined>(undefined);
  columns = signal<TableColumns[]>(
    POSITION_LIST_COLUMNS.filter(col =>
      this.isPositionStatuesEnhancementEnabled()
        ? true
        : col.identifier !== POSITION_LIST_COLUMN_IDENTIFIERS.FILLED_STATUS
    ).map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    POSITION_LIST_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  selectedPosition = signal<PositionListItem | undefined>(undefined);
  isPositionStateLoading = signal<boolean>(false);

  deactivatePositionForm = new FormGroup({
    replacementPositionId: new FormControl<number | string | null>(
      null,
      Validators.required
    ),
  });

  readonly replacementPositionApi = computed(() => {
    const positionId = this.selectedPosition()?.id;
    return positionId
      ? `${ENDPOINTS.Lookup.Positions}?filters[not]=${positionId}&filters[not_child_for]=${positionId}&filters[state]=active`
      : `${ENDPOINTS.Lookup.Positions}?filters[state]=active`;
  });

  actionItemsMapped = signal<ActionList[]>(
    ACTIONS_MENU_ITEMS.map(item => ({
      ...item,
      label: this.transloco.translate(item.label),
    }))
  );

  actionMenuItems = computed(() => {
    const position = this.selectedPosition();
    const actionItemsMapped = this.actionItemsMapped();

    let actionItems: ActionList[] = [];

    if (!position) {
      return actionItems;
    }

    const viewDetailsAction = {
      ...actionItemsMapped[ACTIONS_INDEXES.VIEW],
      routerLinks: [
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.POSITIONS.DETAILS}/${position.id.toString()}`
        ),
      ],
    };

    const editAction = {
      ...actionItemsMapped[ACTIONS_INDEXES.EDIT],
      hasDivider: true,
      routerLinks: [
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.POSITIONS.FORM}/${position.id.toString()}`
        ),
      ],
    };

    actionItems = [viewDetailsAction, editAction];

    if (position.state?.value === 'active') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DEACTIVATE]);
    } else if (position.state?.value === 'inactive') {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.ACTIVATE]);
    }

    if (
      this.isDeleteInactiveEnabled() &&
      position.state?.value === 'inactive'
    ) {
      actionItems.push(actionItemsMapped[ACTIONS_INDEXES.DELETE]);
    }

    return actionItems;
  });

  getPositionList$ = () => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, tab, search } = this.payload();

    return this.positionService
      .getPositionList(page, size, sortColumn, tab?.key, search)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  getPositionList() {
    this.getPositionList$().subscribe({
      next: (res: ApiResponse<PositionListItem>) =>
        this.handlePositionListSuccess(res),
    });
  }

  handlePositionListSuccess(res: ApiResponse<PositionListItem>) {
    this.positionList.set(res.data);
    this.meta.set(res.meta);
  }

  handleAction($event: string, row: PositionListItem) {
    this.selectedPosition.set(row);

    switch ($event) {
      case 'view':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.POSITIONS.DETAILS}`),
          row.id,
        ]);
        break;
      case 'edit':
        this.router.navigate([
          this.tenantService.adminPath(`${ADMIN_ROUTES.POSITIONS.FORM}`),
          row.id,
        ]);
        break;
      case 'deactivate':
        this.initDeactivateForm();
        this.openModal('deactivate');
        break;
      case 'activate':
        this.openModal('activate');
        break;
      case 'delete':
        this.openModal('delete');
        break;
    }
  }

  initDeactivateForm() {
    this.deactivatePositionForm.reset();
  }

  openModal(type: 'activate' | 'deactivate' | 'delete') {
    const customizeContent =
      type === 'deactivate'
        ? this.deactivateModal()
        : type === 'activate'
          ? this.activateModal()
          : this.deleteModal();

    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent,
      width: '24',
    });
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deactivatePosition() {
    const replacementPositionId =
      this.deactivatePositionForm.controls.replacementPositionId.value;
    if (!replacementPositionId) {
      return;
    }

    this.updatePositionState(positionId =>
      this.positionService.deactivatePosition(
        positionId,
        Number(replacementPositionId)
      )
    );
  }

  activatePosition() {
    this.updatePositionState(positionId =>
      this.positionService.activatePosition(positionId)
    );
  }

  deletePosition() {
    this.updatePositionState(positionId =>
      this.positionService.deletePosition(positionId)
    );
  }

  updatePositionState(request: (positionId: string) => Observable<unknown>) {
    const positionId = this.selectedPosition()?.id;
    if (!positionId) {
      return;
    }

    this.isPositionStateLoading.set(true);
    request(positionId.toString())
      .pipe(
        finalize(() => this.isPositionStateLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.getPositionList();
          this.closeModal();
        },
        error: (error: { message?: string }) => {
          this.toaster.add(error.message ?? '', '', '', 'error', true, true);
        },
      });
  }

  viewPositionDetails(position: PositionListItem) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `${ADMIN_ROUTES.POSITIONS.DETAILS}/${position.id}`
      )
    );
  }
}
